import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { from, Observable, of } from 'rxjs';
import {
    catchError,
    concatMap,
    debounceTime,
    distinctUntilChanged,
    exhaustMap,
    filter,
    first,
    map,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs/operators';

import {
    DetalleProductos,
    ICallableParams,
    ICategories,
    IDataCurrentOrders,
    IDataEcommerceConfig,
    IGroup,
    IGroupers,
    IProductos,
    IPromosEcommerce,
    IRecommender,
    IRespOrder,
    IRespProductos,
    IResultCurrentOrders,
    IResultDetailOrder,
    IResultEcommerceConfig,
    IResultPromosEcommerce,
    IResultSendOrder,
    PedidoActualEntity,
} from '../../private/shared/models/productos';
import { ECOMMERCE_ENDPOINTS, GETS, NativeStorageNamesV2 } from '../../shared/models/Constants';
import { OperationsService } from '../../shared/services/operations/operations.service';
import { SharedServiceService } from '../../shared/services/shared-service.service';
import {
    addingProductsToCartState,
    addingProductsToResumeState,
    clearCartProducts,
    detailOrder,
    errorCancelaPedidos,
    errorDetailOrder,
    errorEcommerceConfig,
    errorGetProducts,
    errorObtienePedidos,
    errorPromosEcommerce,
    errorSendCart,
    errorSendOrder,
    errorSendWebhook,
    getProducts,
    getPromosEcommerce,
    sendCancelaPedidos,
    sendCart,
    sendEcommerceConfig,
    sendObtienePedidos,
    sendOrderState,
    sendWebhook,
    setClearCart,
    setDateBD,
    setIDPedido,
    successCancelaPedidos,
    successDetailOrder,
    successEcommerceConfig,
    successGetProducts,
    successObtienePedidos,
    successPromosEcommerce,
    successSendCart,
    successSendOrder,
    successSendWebhook,
} from '../actions';
import { AppState } from '../app-reducers';
import { CartProductsState } from '../reducers';
import { selectEcommerceConfigState } from '../selectors/ecommerce-config.selectors';
import { selectOrdersState } from '../selectors/ecommerce-orders.selectors';
import { IRequestEndpoint } from './../../shared/models/request.model';
import { EncryptionService } from './../../shared/services/encryption/encryption.service';
import { StorageData } from './../../shared/services/utilities/storage-data.class';

@Injectable()
export class ProductsListEffets {
    constructor(
        private actions$: Actions
        , private sharedS: SharedServiceService
    ) { }

    public getProducts$ = createEffect(
        () => this.actions$.pipe(
            ofType(getProducts),
            map(actions => ({ ...actions, params: { offset: (moment().utcOffset() / 60), } })),
            exhaustMap(
                (actions) => from(StorageData.getDataStorage(actions.endpoint.url))
                    .pipe(
                        switchMap((storage: ISaveProducts) => this.DateBD$.pipe(map(date => ({ storage, date })))),
                        switchMap(({ storage, date }) => this.getStorage(storage, actions, date)),
                        map(resp => successGetProducts(resp)),
                        catchError((err: HttpErrorResponse) => of(errorGetProducts({ payload: err.error })))
                    )
            )
        )
    )

    private DateBD$ = of(moment().add(5, 'minutes').format('YYYYMMDDHH'));

    private getStorage = (storage: ISaveProducts, actions: ICallableParams, date: string) => {
        if (this.sharedS.forceRefresh) {
            return this.sharedS.executeRequestByUser(actions.endpoint, actions['params'])
                .pipe(switchMap((resp: IRespProductos) => this.saveDataStorage(actions, resp, date)));
        }
        if (storage && !(typeof storage.data === "object" && Object.keys(storage.data).length === 0)) {
            if (storage.fecha === date) {
                return of({
                    success: true,
                    status: 'resp/valid-datos',
                    message: 'Se obtuvieron los datos correctamente del Storage',
                    data: storage.data
                });
            } else {
                return this.sharedS.executeRequestByUser(actions.endpoint, actions['params'])
                    .pipe(switchMap((resp: IRespProductos) => this.saveDataStorage(actions, resp, date)));
            }
        } else {
            return this.sharedS.executeRequestByUser(actions.endpoint, actions['params'])
                .pipe(switchMap((resp: IRespProductos) => this.saveDataStorage(actions, resp, date)));
        }
    }

    private saveDataStorage = (actions: ICallableParams, dataNew: IRespProductos, date: string): Observable<IRespProductos> =>
        this.sharedS.isUrlWithTokenWA$.pipe(
            take(1),
            map(isUrl => {
                if (!isUrl || dataNew.hasOwnProperty('data')) {
                    StorageData.setDataStorage(actions.endpoint.url, { data: dataNew.data || [], fecha: date });
                }
                return dataNew;
            })
        )

}

/**
 * Promociones ecommerce
 */
@Injectable()
export class PromosEcommerceEffets {
    private keyStorage = NativeStorageNamesV2.ObtienePromocionesEcommerce;

    constructor(
        private actions$: Actions
        , private translate: TranslateService
        , private httpS: SharedServiceService
        , private store: Store<AppState>
    ) { }

    public getPromosEcommerce$ = createEffect(
        () => this.actions$.pipe(
            ofType(getPromosEcommerce),
            exhaustMap(
                () => this.store.select(selectOrdersState)
                    .pipe(
                        filter(x => x.success || x.failed),
                        first(),
                        switchMap(() => from(StorageData.getDataStorage(this.keyStorage))
                            .pipe(
                                concatMap((storage: ISavePromos) => this.httpS.getStorage(storage, this.keyStorage, this.translate.instant('Miscellaneous.updateinterval5'), 'executeRequestByUser', ECOMMERCE_ENDPOINTS.get_promos, null))
                            )
                        ),
                        map((resp: IResultPromosEcommerce) => successPromosEcommerce(resp)),
                        catchError((err: HttpErrorResponse) => of(errorPromosEcommerce({ payload: err.error })))
                    )
            ),
        )
    )

}

/**
 * Envio del pedido
 */

@Injectable()
export class PedidosEffets {
    constructor(
        private actions$: Actions
        , private sharedS: SharedServiceService
        , private operationsS: OperationsService
        , private encryptionS: EncryptionService
        , private store: Store<AppState>
    ) { }

    /**
     * Obtiene DETALLE del pedido
     */
    public detailOrder$ = createEffect(
        () => this.actions$.pipe(
            ofType(detailOrder),
            tap(() => this.store.dispatch(setClearCart({ clearCart: false }))),
            switchMap(
                (actions) => this.sharedS.executeRequestByUser(actions.endpoint, { ID_Pedido: actions.respOrder.id_pedido })
                    .pipe(
                        tap((resp: IResultDetailOrder) => {
                            const cartProductos: IProductos[] = resp.data.Productos.map(this.formatResult.bind(this));
                            const cartPromos: IProductos[] = resp.data.ProductsPromos.map(this.formatResult.bind(this));
                            this.store.dispatch(addingProductsToResumeState({ cartProductos, cartPromos, promo: resp.data.Promos, pedidoActual: {} as PedidoActualEntity }));
                        }),
                        map((resp: IResultDetailOrder) => successDetailOrder({ respOrder: actions.respOrder as IRespOrder })),
                        catchError((err: HttpErrorResponse) => of(errorDetailOrder({ payload: err.error })))
                    )
            )
        )
    );

    /**
     * Guardado del Carrito
     */
    public sendCart$ = createEffect(
        () => this.actions$.pipe(
            ofType(sendCart),
            debounceTime(500),
            withLatestFrom(this.store.select((state: AppState) => state.cartProducts)), // ? Combina la acción con el valor más reciente del store (sin reiniciar el stream)
            map(([_, cart]) => {
                const filledCart = this.fillCartState(cart);
                return {
                    endpoint: ECOMMERCE_ENDPOINTS.create_cart,
                    Pedido: this.formatCartOrder(filledCart.cartProductos.concat(filledCart.cartPromos)),
                    Promos: this.formatPromo(filledCart.promo),
                };
            }),
            distinctUntilChanged((prev, curr) => prev.Pedido.map(p => p.Hash).join() === curr.Pedido.map(p => p.Hash).join()),
            concatMap(actions => this.sharedS.executeRequestByUser(actions.endpoint, actions)
                .pipe(
                    map(resp => successSendCart(resp)),
                    catchError((err: HttpErrorResponse) => of(errorSendCart({ payload: err.error })))
                )
            )
        )
    );

    /**
     * Envio del Pedido
     */
    public sendOrderState$ = createEffect(
        () => this.actions$.pipe(
            ofType(sendOrderState),
            exhaustMap(
                (actions) => this.operationsS.combinerCartOrder$()
                    .pipe(
                        take(1),
                        map(cart => {
                            const pedidos = cart.cartProductos.concat(cart.cartPromos).filter(p => p.productos > 0);
                            const promosFiltradas = cart.promo.filter(p => p.PaquetesCanjeados > 0);

                            return {
                                endpoint: actions.endpoint as IRequestEndpoint,
                                payload: {
                                    offset: (moment().utcOffset() / 60),
                                    pedido: this.formatOrder(pedidos),
                                    promos: this.formatPromo(promosFiltradas)
                                },
                                cartOriginal: cart
                            };
                        }),
                        exhaustMap((data) =>
                            this.sharedS.executeRequestByUser(data.endpoint, data.payload)
                                .pipe(
                                    map((resp: IResultSendOrder) => successSendOrder(resp)),
                                    concatMap((resp: IResultSendOrder) => [
                                        addingProductsToResumeState(data.cartOriginal),
                                        successSendOrder(resp)
                                    ]),
                                    catchError((err: HttpErrorResponse) => of(errorSendOrder({ payload: err.error })))
                                )
                        )
                    )
            )
        )
    );

    private formatOrder = (pedidos: IProductos[]) => pedidos.map(product => {
        const Descuento = product.ClavePromo && product.precio_original ? this.operationsS.twoDecimal(product.precio_original - product.precio) : this.operationsS.twoDecimal(product.price_discount || 0);
        const precioFormateado = this.operationsS.twoDecimal(product.precio);
        const Hash = this.encryptionS.hmacSha256(`${this.sharedS.bodegaNud$.value.ID_Bodega}${this.sharedS.bodegaNud$.value.NUD}${product.id_producto}${product.productos}${precioFormateado}${Descuento}`);

        return {
            id_producto: String(product.id_producto),
            quantity: product.productos,
            unit_price: precioFormateado,
            ClavePromo: product.ClavePromo,
            Descuento,
            DiscountType: product.hasOwnProperty('discount_type') ? product.discount_type : null,
            DiscType: product.hasOwnProperty('discType') ? product.discType : null,
            DiscId: product.hasOwnProperty('discId') ? product.discId : null,
            Nivel: product.Nivel || (product.hasOwnProperty('discLevel') ? product.discLevel : null),
            modelo: product.modelo,
            Etiquetados: product.Etiquetados,
            KeyEtiquetados: product.KeyEtiquetados,
            Multiplicador: product.Multiplicador,
            Hash,
        }
    });

    private formatPromo = (promos: IPromosEcommerce[]) => promos.map(promo => ({
        ClavePromo: promo.ClavePromo,
        PaquetesCanjeados: promo.PaquetesCanjeados,
        Bonificacion: promo.Bonificacion,
        CartAuto: promo.CartAuto,
        Hash: this.encryptionS.hmacSha256(`${this.sharedS.bodegaNud$.value.ID_Bodega}${this.sharedS.bodegaNud$.value.NUD}${promo.ClavePromo}${promo.PaquetesCanjeados}`)
    }));

    private formatCartOrder = (pedidos: IProductos[]) => pedidos.map(product => ({
        ClaveProducto: product.clave_producto,
        ClavePromo: product.ClavePromo,
        Quantity: product.productos,
        Nivel: product.Nivel,
        Modelo: product.modelo,
        Hash: this.encryptionS.hmacSha256(`${this.sharedS.bodegaNud$.value.ID_Bodega}${this.sharedS.bodegaNud$.value.NUD}${product.clave_producto}${product.productos}`)
    }))
        .sort((a, b) =>
            String(a.ClaveProducto).localeCompare(String(b.ClaveProducto)) ||
            String(a.ClavePromo).localeCompare(String(b.ClavePromo))
        );

    private formatResult = (element: DetalleProductos) => ({
        id_producto: element.id_producto,
        title: element.title,
        subtitle: element.subtitle,
        imagen: element.imagen,
        precio: this.operationsS.twoDecimal(element.unit_price),
        productos: element.quantity,
        sku: element.flavors,
        ClaveProducto: element.ClaveProducto,
        ClavePromo: element.ClavePromo,
        precio_original: this.operationsS.twoDecimal(element.precio_original),
        Maximo: element.Maximo
    });

    private fillCartState = (cart: CartProductsState): CartProductsState => {
        const actualPrts = cart.cartProductos.filter(p => !p.isCarrito);
        const actualPrtsPromos = cart.cartPromos.filter(p => !p.isCarrito);
        const actualPromo = cart.promo.filter(p => !p.isCarrito);

        let cartProductos = cart.cartProductos.filter(p => p.isCarrito);
        let cartPromos = cart.cartPromos.filter(p => p.isCarrito);
        let cartpromo = cart.promo.filter(p => p.isCarrito);

        cartProductos = cartProductos.map(cp => {
            const index = actualPrts.findIndex(x => x.id_producto === cp.id_producto);
            if (index >= 0) {
                return {
                    ...cp,
                    productos: cp.productos > 0 ? cp.productos - actualPrts[index].productos : cp.productos,
                };
            } else {
                return cp;
            }
        }).filter(ap => ap.productos !== 0);
        cartPromos = cartPromos.map(cp => {
            const index = actualPrtsPromos.findIndex(x => x.clave_producto === cp.clave_producto && x.Nivel === cp.Nivel && x.ClavePromo === cp.ClavePromo);
            if (index >= 0) {
                return {
                    ...cp,
                    productos: cp.productos > 0 ? cp.productos - actualPrtsPromos[index].productos : cp.productos,
                };
            } else {
                return cp;
            }
        }).filter(ap => ap.productos !== 0);
        cartpromo = cartpromo.map(cp => {
            const index = actualPromo.findIndex(x => x.ClavePromo === cp.ClavePromo);
            if (index >= 0) {
                return {
                    ...cp,
                    PaquetesCanjeados: cp.PaquetesCanjeados > 0 ? cp.PaquetesCanjeados - actualPromo[index].PaquetesCanjeados : cp.PaquetesCanjeados,
                };
            } else {
                return cp;
            }
        }).filter(ap => ap.PaquetesCanjeados !== 0);

        return { cartProductos, cartPromos, promo: cartpromo };
    };
}

/**
 * Obtiene Pedidos
 */
@Injectable()
export class ObtienePedidosEffets {
    constructor(
        private actions$: Actions
        , private httpS: SharedServiceService
        , private operationsS: OperationsService
        , private store: Store<AppState>
    ) { }

    public sendObtienePedidos$ = createEffect(
        () => this.actions$.pipe(
            ofType(sendObtienePedidos),
            exhaustMap(
                () => {
                    const params = { offset: (moment().utcOffset() / 60) }
                    return this.httpS.executeRequestByUser(ECOMMERCE_ENDPOINTS.get_orders_v3, params)
                        .pipe(
                            map((resp: IResultCurrentOrders) => {
                                const ordersData = (resp?.data || {}) as IDataCurrentOrders
                                return successObtienePedidos({ data: ordersData })
                            }),
                            catchError((err: HttpErrorResponse) => of(errorObtienePedidos({ payload: err.error })))
                        )
                }
            )
        )
    );

    public processCartOrder$ = createEffect(
        () => this.actions$.pipe(
            ofType(successObtienePedidos),
            concatMap(action => this.store.select(selectEcommerceConfigState)
                .pipe(
                    filter(x => x.success || x.failed),
                    first(),
                    map(configState => ({ orders: action.data, configState }))
                )
            ),
            tap(({ orders, configState }) => {
                if (configState.success) {
                    this.setCartOrder(orders, configState.respConfig)
                } else {
                    console.warn('[processCartOrder$] Carrito no procesado: config falló', configState.error)
                }
            }),
        ),
        { dispatch: false }
    );

    private setCartOrder = (orders: IDataCurrentOrders, config: IDataEcommerceConfig) => {
        this.store.dispatch(clearCartProducts());
        this.store.dispatch(setDateBD({ DateBD: config.HOY }));
        this.operationsS.LimitMinMonto = config.LimitMinMonto;
        this.operationsS.LimitMinCajas = config.LimitMinCajas;
        this.operationsS.LimitMaxMonto = config.LimitMaxMonto;
        this.operationsS.LimitMaxCajas = config.LimitMaxCajas;
        this.operationsS.LimiteMinMontoAdvertencia = config.LimiteMinAdvertencia;
        const pedido_actual = (orders.pedido_actual || []).filter(pa => pa.status === 1);
        const carrito_actual = orders.carrito_actual || [];
        if (pedido_actual.length < 1 && (carrito_actual.length < 1 || (carrito_actual.length > 0 && carrito_actual[0].detalle.length < 1 && carrito_actual[0].ProductsPromos.length < 1))) {
            StorageData.removeDataStorage(NativeStorageNamesV2.CartItemsAnalytics);
        }
        let actualPrts: IProductos[] = [];
        let actualPrtsPromos: IProductos[] = [];
        let actualPromo = [];
        if (pedido_actual.length > 0) { // ? Agrega productos del pedido al estado
            actualPrts = pedido_actual[0].detalle.map(p => this.operationsS.formatResult(p, false));

            actualPrtsPromos = pedido_actual[0].ProductsPromos.map(p => this.operationsS.formatResult(p, false));
            actualPromo = pedido_actual[0].Promos.map(p => ({ ...p, isCarrito: false }));
            this.store.dispatch(addingProductsToCartState({ cartProductos: actualPrts, cartPromos: actualPrtsPromos, promo: actualPromo }));
        }
        const pedido: PedidoActualEntity = pedido_actual.length < 1 ? {} as PedidoActualEntity : { ...pedido_actual[0], detalle: [], Promos: [], ProductsPromos: [] };
        this.store.dispatch(setIDPedido({ pedidoActual: pedido, fecha_entrega: config.fecha_entrega_copy }));
        const containCart = carrito_actual.length > 0 ? carrito_actual[0].detalle.concat(carrito_actual[0].ProductsPromos).length > 0 : false;

        if (pedido_actual.length > 0 && containCart) { // ? Combina productos de pedido actual con carrito
            const cartProductos: IProductos[] = carrito_actual[0].detalle
                .map(p => this.operationsS.formatResult(p, true))
                .map(p => {
                    const index = actualPrts && actualPrts.length > 0 ? actualPrts.findIndex(x => x.id_producto === p.id_producto) : -1;
                    if (index >= 0) {
                        const productosCombine = (p.productos + actualPrts[index].productos) === 0 ? p.productos : (p.productos + actualPrts[index].productos);
                        return {
                            ...p,
                            productos: productosCombine,
                        };
                    } else {
                        return p;
                    }
                });
            const cartPromos: IProductos[] = carrito_actual[0].ProductsPromos
                .map(p => this.operationsS.formatResult(p, true))
                .map(p => {
                    const index = actualPrtsPromos && actualPrtsPromos.length > 0 ? actualPrtsPromos.findIndex(x => x.id_producto === p.id_producto && x.ClavePromo === p.ClavePromo && x.Nivel === p.Nivel) : -1;
                    if (index >= 0) {
                        const productosCombine = (p.productos + actualPrtsPromos[index].productos) === 0 ? p.productos : (p.productos + actualPrtsPromos[index].productos);
                        return {
                            ...p,
                            productos: productosCombine,
                        };
                    } else {
                        return p;
                    }
                });
            const cartPromo = carrito_actual[0].Promos.map(promo => {
                const index = actualPromo.length > 0 ? actualPromo.findIndex(x => x.ClavePromo === promo.ClavePromo) : -1;
                if (index >= 0) {
                    const canjeCombine = (promo.PaquetesCanjeados + actualPromo[index].PaquetesCanjeados) === 0 ? promo.PaquetesCanjeados : (promo.PaquetesCanjeados + actualPromo[index].PaquetesCanjeados);
                    return {
                        ...promo,
                        PaquetesCanjeados: canjeCombine,
                        isCarrito: true,
                    };
                } else {
                    return { ...promo, isCarrito: true };
                }
            });
            this.store.dispatch(addingProductsToCartState({ cartProductos, cartPromos, promo: cartPromo }));
        } else if (containCart) { // ? Añade solo productos de carrito
            const cartProductos: IProductos[] = carrito_actual[0].detalle.map(p => this.operationsS.formatResult(p, true)).filter(p => p.productos > 0);
            const cartPromos: IProductos[] = carrito_actual[0].ProductsPromos.map(p => this.operationsS.formatResult(p, true)).filter(p => p.productos > 0);
            this.store.dispatch(addingProductsToCartState({ cartProductos, cartPromos, promo: carrito_actual[0].Promos.filter(p => p.PaquetesCanjeados > 0).map(p => ({ ...p, isCarrito: true })) }));
        }
    }

}

/**
 * Cancel Pedidos
 */
@Injectable()
export class CancelPedidosEffets {
    constructor(
        private actions$: Actions
        , private httpS: SharedServiceService
    ) { }

    public sendCancelaPedidos$ = createEffect(
        () => this.actions$.pipe(
            ofType(sendCancelaPedidos),
            map(actions => ({ ...actions, params: { offset: (moment().utcOffset() / 60), } })),
            switchMap(
                (actions) => this.httpS.executeRequestByUser(actions.endpoint, actions.params)
                    .pipe(
                        map(resp => successCancelaPedidos(resp)),
                        catchError((err: HttpErrorResponse) => of(errorCancelaPedidos({ payload: err.error })))
                    )
            )
        )
    );

}

/**
 * Manda informaión al Webhook
 */
@Injectable()
export class SendWebhookEffets {
    constructor(
        private actions$: Actions
        , private httpS: SharedServiceService
    ) { }

    public sendWebhook$ = createEffect(
        () => this.actions$.pipe(
            ofType(sendWebhook),
            switchMap(
                (actions) => this.httpS.executeFunctionDataUser(GETS.SendOrderWebhook, actions)
                    .pipe(
                        map(resp => successSendWebhook(resp)),
                        catchError((err: Error) => of(errorSendWebhook({ payload: err })))
                    )
            )
        )
    );

}

/**
 * Ecommerce Config
 */
@Injectable()
export class EcommerceConfigEffets {
    constructor(
        private actions$: Actions
        , private httpS: SharedServiceService
    ) { }

    public sendEcommerceConfig$ = createEffect(
        () => this.actions$.pipe(
            ofType(sendEcommerceConfig),
            exhaustMap(
                () => this.httpS.executeRequestByUser(ECOMMERCE_ENDPOINTS.get_configuration_ecommerce, {})
                    .pipe(
                        map((resp: IResultEcommerceConfig) => {
                            const configData = (resp?.data || {}) as IDataEcommerceConfig
                            return successEcommerceConfig({ data: configData })
                        }),
                        catchError((err: HttpErrorResponse) => of(errorEcommerceConfig({ payload: err.error })))
                    )
            )
        )
    );
}

interface ISaveProducts {
    data: [
        IProductos[],
        IGroup[],
        IProductos[]?,
        IProductos[]?,
        IRecommender[]?,
        ICategories[]?,
        IGroupers[]?,
    ]
    fecha: string;
}

interface ISavePromos {
    fecha: string;
    data: IPromosEcommerce[];
}
